/**
 * wordExport - pure helpers for the "Export to Word" flow on the Knowledge
 * Graph page. Extracted from KnowledgeGraphPage.jsx in Phase 5E - IJ-461 of
 * umbrella IJ-150 (operator option C: shrink the composition-root handlers).
 *
 * buildSourcesMarkdown and deriveShortTopic are byte-verbatim lifts.
 * buildWordExportPayload composes the verbatim title, version, content and
 * filename logic that used to sit inline in handleExportToWord. All three are
 * pure; the api.post call and the DOM download stay in the parent handler.
 */

export const buildSourcesMarkdown = (result) => {
  if (!result) return '';
  // Brief-shape sources first (Knowledge Controls · BriefPanel)
  if (Array.isArray(result.sources) && result.sources.length > 0) {
    const lines = result.sources.map((s, i) => {
      const id = s.id != null ? s.id : (i + 1);
      const label = (s.label || s.title || s.name || '').toString().trim();
      const url = (s.url || '').toString().trim();
      return url
        ? `${id}. ${label} — ${url}`
        : `${id}. ${label}`;
    });
    const note = (result.sources_note || '').toString().trim();
    return `\n\n## Sources\n\n${note ? note + '\n\n' : ''}${lines.join('\n')}\n`;
  }
  // Web query results fallback (KnowledgeGraph page · WebSourcesBlock)
  if (Array.isArray(result.web_results) && result.web_results.length > 0) {
    const lines = result.web_results.map((r, i) => {
      const name = (r.source_name || r.title || 'Untitled').toString().trim();
      const title = (r.title || '').toString().trim();
      const url = (r.url || '').toString().trim();
      const parts = [`${i + 1}. ${name}`];
      if (title && title !== name) parts.push(`— ${title}`);
      if (url) parts.push(`(${url})`);
      return parts.join(' ');
    });
    return `\n\n## Sources\n\n${lines.join('\n')}\n`;
  }
  return '';
};

export const deriveShortTopic = (raw) => {
  if (!raw) return '';
  let s = String(raw);
  // Strip the chip-appended clauses ("— Time Scope: Last 2 years; …")
  s = s.split(/\s+—\s+/)[0];
  // Strip the "Provide / Generate a strategy brief on/about/for" preamble
  s = s.replace(/^\s*(?:please\s+)?(?:provide|give|generate|create|write|do|produce|draft)\s+(?:me\s+)?(?:a|an|the)?\s*(?:strategy|notification|executive|research|market|short|brief)?\s*brief\s+(?:on|about|for|regarding|of)\s+/i, '');
  // Stop at first sentence-end (. ! ?)
  s = s.split(/[.!?]/)[0];
  // Strip trailing "Include …", "Provide …", "Cover …", "Add …"
  s = s.replace(/[,;:]?\s*(include|provide|cover|add|incorporate)\b.*$/i, '');
  // Tidy and cap
  s = s.replace(/[,.;:]+$/, '').replace(/\s{2,}/g, ' ').trim();
  if (!s) return '';
  // Title-case
  s = s.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  if (s.length > 60) s = s.slice(0, 60).replace(/\s+\S*$/, '');
  return s;
};

export const buildWordExportPayload = (threadItem, now = new Date()) => {
  if (!threadItem?.result?.answer) return null;
  let title = 'Query Results';
  const content = threadItem.result.answer;
  const queryText = threadItem.query || '';

  // For briefs: extract the title from the # heading or ## Subject line
  // The brief format is: # Title\n\n## Subject\nTopic
  const briefTitleMatch = content.match(/^# (.+?)(?:\n|$)/m);
  const subjectMatch = content.match(/## Subject\s*\n+(.+?)(?:\n|$)/i);

  // For brief queries: use the user's named topic from the query
  const briefQueryMatch = queryText.match(/brief\s+(?:on|about|for|regarding)\s+(.+)/i);

  if (queryText.toLowerCase().includes('brief')) {
    // Brief document: prioritize user's query topic, then content Subject, then heading
    if (briefQueryMatch) {
      title = briefQueryMatch[1].trim().replace(/[.!?]+$/, '');
      title = title.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    } else if (subjectMatch && subjectMatch[1].trim().length > 0 && subjectMatch[1].trim().toLowerCase() !== 'brief') {
      title = subjectMatch[1].trim();
    } else if (briefTitleMatch && briefTitleMatch[1].trim().length > 0) {
      title = briefTitleMatch[1].trim();
    } else {
      title = 'Brief';
    }
  } else {
    // Non-brief: use heading or first bold text
    const headerMatch = content.match(/^#+ (.+?)(?:\n|$)/m);
    const boldMatch = content.match(/\*\*([^*]+)\*\*/);
    if (headerMatch && headerMatch[1].length < 100) {
      title = headerMatch[1].trim();
    } else if (boldMatch && boldMatch[1].length < 60) {
      title = boldMatch[1].trim();
    }
  }

  // Clean up title
  title = title.replace(/[#*_]/g, '').trim();
  if (!title) title = 'Brief';
  const shortTopic = deriveShortTopic(queryText) || title;
  const wordTitle = `Subject Matter \u2014 ${shortTopic}`;
  const versionRaw = (threadItem.result.version || 'V1.0').trim();
  const d = now;
  const dd = String(d.getDate()).padStart(2, '0');
  const monAbbr = d.toLocaleString('en-CA', { month: 'short' });
  const yyyy = d.getFullYear();
  const vSlug = versionRaw.toLowerCase();
  const safeTopic = shortTopic.replace(/[/\\:*?"<>|]+/g, '').trim();
  const filename = `Subject Matter - ${safeTopic}_${dd}_${monAbbr}_${yyyy}_${vSlug}.docx`;
  return {
    content: threadItem.result.answer + buildSourcesMarkdown(threadItem.result),
    title: wordTitle,
    version: versionRaw,
    filename,
  };
};
